import { Button } from '@HoudiniUI/Button';
import { Input } from '@HoudiniUI/Input';
import { Pill } from '@HoudiniUI/Pill';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import { toast } from 'sonner';
import { groupAICampaignRecipients, IAICampaignRecipient } from '../../../models/AICampaign';
import { createContentStateWithDocument } from '../../../models/utils/richContentUtils';
import {
	invalidateAICampaignDetails,
	invalidateAICampaignRecipients,
	useAICampaignDetails,
	useAICampaignRecipientEmail,
	useAICampaignRecipients,
	useRemoveCampaignRecipient,
	useStartAICampaignGeneration,
	useUpdateCampaignRecipient,
} from '../../../queries/AICampaign';
import { AICampaignGenerationProgress } from '../templates/AICampaignGenerationProgress';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { RichContentDocumentEditor } from '../../components/richContent/RichContentDocumentEditor';
import { baseStyleSheet } from '../../styles/styles';
import { styleSheet } from './AICampaignQueuedView.styles';

/** Rows rendered before the list waits for a scroll; a campaign can have thousands of recipients. */
const RecipientPageSize = 50;

interface IProps {
	campaignId: string;
	onCancelCampaign?(e?: React.MouseEvent<HTMLElement>): void;
	onChangeSchedule?(): void;
}

/**
 * The queued view of an AI campaign. An ordinary campaign shows its one composed email here, but an AI campaign's
 * emails are written per recipient, so the left pane previews whichever recipient is selected on the right, starting
 * with the first, and editing or removing applies to the email being read. Before generation there is nothing to
 * preview yet; the panes explain what will happen, offer to write the emails now, and follow the run's progress
 * until the recipients take their place.
 */
export const AICampaignQueuedView: React.FC<IProps> = ({ campaignId, onCancelCampaign, onChangeSchedule }) => {
	// Fetched here rather than taken from the caller's view model so that generation finishing (which invalidates
	// these details) flips this screen to the recipient list without anyone reloading the modal.
	const { data: details } = useAICampaignDetails({ bulkEmailMessageId: campaignId });
	const isGenerated = !!details?.aiBrief?.lastGeneratedDate;
	const campaignName = details?.name;
	const scheduleStartDate = details?.schedule?.startDate;

	const [startedJobId, setStartedJobId] = useState<string | null>(null);
	const generateMutation = useStartAICampaignGeneration({
		onError: error => toast.error(error?.systemMessage || 'Could not start writing these emails'),
		onSuccess: job => setStartedJobId(job.jobId),
	});
	const generationJobId = startedJobId ?? details?.aiBrief?.generationJobId;

	const { data: recipients, isLoading: recipientsLoading } = useAICampaignRecipients({
		bulkEmailMessageId: campaignId,
		enabled: isGenerated,
		includeLeftOut: true,
	});

	const {
		recipients: rows,
		leftOut,
		writtenCount,
		failedCount,
		skippedCount,
	} = useMemo(() => groupAICampaignRecipients(recipients ?? []), [recipients]);
	const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
	const [visibleCount, setVisibleCount] = useState(RecipientPageSize);
	const [visibleLeftOutCount, setVisibleLeftOutCount] = useState(RecipientPageSize);
	const [isEditing, setIsEditing] = useState(false);
	const [draftSubject, setDraftSubject] = useState('');
	const [draftContent, setDraftContent] = useState<Api.IRichContentEditorState | null>(null);

	useEffect(() => {
		if (!selectedContactId && rows.length > 0) {
			setSelectedContactId((rows.find(r => r.hasContent) ?? rows[0]).contactId);
		}
	}, [rows, selectedContactId]);

	const selected = rows.find(r => r.contactId === selectedContactId) ?? null;
	const {
		data: email,
		isLoading: emailLoading,
		isSuccess: emailLoaded,
	} = useAICampaignRecipientEmail({
		bulkEmailMessageId: campaignId,
		contactId: selectedContactId ?? '',
		enabled: isGenerated && !!selected?.hasContent,
	});

	const readOnlyContent = useMemo(() => createContentStateWithDocument(email?.htmlContent ?? ''), [email?.htmlContent]);

	const updateMutation = useUpdateCampaignRecipient({
		onError: error => toast.error(error?.systemMessage || 'Could not save this email'),
		onSuccess: () => {
			toast.success('Email updated');
			setIsEditing(false);
		},
	});

	const removedNameRef = useRef('Recipient');
	const removeMutation = useRemoveCampaignRecipient({
		onError: error => toast.error(error?.systemMessage || 'Could not remove this recipient'),
		onSuccess: () => toast.success(`${removedNameRef.current} removed from this campaign`),
	});

	const removeSelected = () => {
		if (!selected) {
			return;
		}

		const index = rows.findIndex(r => r.contactId === selected.contactId);
		removedNameRef.current = selected.name || 'Recipient';
		// Selection moves off the recipient before the request: removal drops their cached email and refreshes the
		// list, and a still-selected recipient would refetch an email the API can only 404 on.
		setSelectedContactId(rows[index + 1]?.contactId ?? rows[index - 1]?.contactId ?? null);
		removeMutation.mutate({ bulkEmailMessageId: campaignId, contactId: selected.contactId });
	};

	const selectRecipient = (contactId: string) => {
		setIsEditing(false);
		setSelectedContactId(contactId);
	};

	// Only editable once the body has actually loaded. Opening the editor on a failed fetch would show an empty
	// draft, and saving it would overwrite the recipient's real email with nothing.
	const startEditing = () => {
		if (!emailLoaded) {
			return;
		}

		setDraftSubject(email?.subject ?? selected?.subject ?? '');
		setDraftContent(createContentStateWithDocument(email?.htmlContent ?? ''));
		setIsEditing(true);
	};

	const save = () => {
		if (!draftContent || !selectedContactId) {
			return;
		}

		updateMutation.mutate({
			bulkEmailMessageId: campaignId,
			contactId: selectedContactId,
			subject: draftSubject,
			content: draftContent.getRawRichTextContent(),
		});
	};

	const renderPreview = () => {
		if (!isGenerated) {
			return (
				<div className={css(styleSheet.emptyState)}>
					<h3 className={css(styleSheet.emptyHeadline)}>The emails have not been written yet</h3>
					<p className={css(styleSheet.emptyBody)}>
						Each recipient gets their own email, written about a day and a half before the send so the research is
						current. They will appear here as soon as they are ready, and you will be able to read, edit, or remove any
						of them before they go out.
					</p>
				</div>
			);
		}

		if (recipientsLoading || (emailLoading && !email)) {
			return (
				<div className={css(styleSheet.emptyState)}>
					<LoadingSpinner type='large' />
				</div>
			);
		}

		if (!selected) {
			return (
				<div className={css(styleSheet.emptyState)}>
					<h3 className={css(styleSheet.emptyHeadline)}>No recipients</h3>
					<p className={css(styleSheet.emptyBody)}>
						{leftOut.length > 0
							? 'No email could be written for anyone in this campaign. The contacts it left out are listed alongside.'
							: 'Every recipient has been removed from this campaign.'}
					</p>
				</div>
			);
		}

		if (!selected.hasContent) {
			return (
				<div className={css(styleSheet.emptyState)}>
					<h3 className={css(styleSheet.emptyHeadline)}>
						No email was written for {selected.name || selected.emailAddress || selected.contactId}
					</h3>
					<p className={css(styleSheet.emptyBody)}>They won’t receive this campaign.</p>
				</div>
			);
		}

		if (isEditing && !!draftContent) {
			return (
				<>
					<Input
						onChange={event => setDraftSubject(event.target.value)}
						placeholder='Subject'
						styles={[styleSheet.editorSubject]}
						value={draftSubject}
					/>
					<RichContentDocumentEditor
						contentState={draftContent}
						onContentStateChanged={setDraftContent}
						styles={[styleSheet.editableEditor]}
					/>
					<div className={css(styleSheet.editActions)}>
						<Button disabled={updateMutation.isPending} onClick={() => setIsEditing(false)} variant='outline'>
							Cancel
						</Button>
						<Button
							disabled={!draftSubject.trim() || updateMutation.isPending}
							isLoading={updateMutation.isPending}
							onClick={save}
							variant='primary'
						>
							Save
						</Button>
					</div>
				</>
			);
		}

		return (
			<>
				<div className={css(styleSheet.previewHeader)}>
					<div className={css(styleSheet.previewMeta)}>
						<span className={css(styleSheet.previewRecipient)}>
							To: {selected.name || selected.emailAddress || selected.contactId}
						</span>
						<span className={css(styleSheet.previewSubject)}>Subject: {email?.subject ?? selected.subject}</span>
					</div>
					<div className={css(styleSheet.previewActions)}>
						<Button
							disabled={writtenCount <= 1 || removeMutation.isPending}
							isLoading={removeMutation.isPending}
							onClick={removeSelected}
							variant='destructive-secondary'
						>
							Remove recipient
						</Button>
						<Button disabled={!emailLoaded} onClick={startEditing} variant='outline'>
							Edit email
						</Button>
					</div>
				</div>
				<RichContentDocumentEditor readOnly contentState={readOnlyContent} styles={[styleSheet.previewBody]} />
			</>
		);
	};

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.previewPane)}>
				{!!campaignName && <h2 className={css(styleSheet.campaignName)}>{campaignName}</h2>}
				{renderPreview()}
			</div>

			<div className={css(styleSheet.rail)}>
				{!!scheduleStartDate && (
					<div className={css(styleSheet.scheduleCard)}>
						<span className={css(styleSheet.scheduleTitle)}>Send Time</span>
						<time dateTime={scheduleStartDate}>
							{moment(new Date(scheduleStartDate)).format('dddd, MMMM Do, YYYY, h:mm A')}
						</time>
						<div className={css(styleSheet.scheduleActions)}>
							{!!onChangeSchedule && (
								<button className={css(baseStyleSheet.brandLink)} onClick={onChangeSchedule} type='button'>
									<span>Change</span>
								</button>
							)}
							{!!onCancelCampaign && (
								<button className={css(styleSheet.cancelLink)} onClick={onCancelCampaign} type='button'>
									<span>Cancel Campaign</span>
								</button>
							)}
						</div>
					</div>
				)}

				{isGenerated ? (
					<div className={css(styleSheet.recipientsCard)}>
						<span className={css(styleSheet.recipientsTitle)}>
							{`${writtenCount.toLocaleString()} ${writtenCount === 1 ? 'recipient' : 'recipients'}`}
						</span>
						{(failedCount > 0 || skippedCount > 0) && (
							<span className={css(styleSheet.recipientsSummary)}>
								{[
									failedCount > 0 ? `${failedCount.toLocaleString()} couldn’t be written` : null,
									skippedCount > 0 ? `${skippedCount.toLocaleString()} left out by AI` : null,
								]
									.filter(Boolean)
									.join(' · ')}
							</span>
						)}
						<div className={css(styleSheet.recipientsList)}>
							{rows.slice(0, visibleCount).map(recipient => (
								<RecipientRow
									isSelected={recipient.contactId === selectedContactId}
									key={recipient.contactId}
									onClick={() => selectRecipient(recipient.contactId)}
									recipient={recipient}
								/>
							))}
							{visibleCount < rows.length && (
								<Waypoint bottomOffset='-200px' onEnter={() => setVisibleCount(count => count + RecipientPageSize)} />
							)}
						</div>
					</div>
				) : (
					<div className={css(styleSheet.noticeCard)}>
						<p className={css(styleSheet.noticeHeadline)}>Every recipient will get their own email</p>
						<p className={css(styleSheet.noticeBody)}>
							The emails are written about a day and a half before the send. You will hear from us when they are ready
							to review.
						</p>
						{generationJobId ? (
							<AICampaignGenerationProgress
								jobId={generationJobId}
								onComplete={() => {
									invalidateAICampaignDetails(campaignId);
									invalidateAICampaignRecipients(campaignId);
								}}
							/>
						) : (
							<Button
								disabled={generateMutation.isPending}
								isLoading={generateMutation.isPending}
								onClick={() => generateMutation.mutate(campaignId)}
								variant='outline'
							>
								Write them now
							</Button>
						)}
					</div>
				)}

				{isGenerated && leftOut.length > 0 && (
					<div className={css(styleSheet.leftOutCard)}>
						<span className={css(styleSheet.recipientsTitle)}>Not receiving this campaign</span>
						<div className={css(styleSheet.recipientsList)}>
							{leftOut.slice(0, visibleLeftOutCount).map(contact => (
								<LeftOutRow contact={contact} key={contact.contactId} />
							))}
							{visibleLeftOutCount < leftOut.length && (
								<Waypoint
									bottomOffset='-200px'
									onEnter={() => setVisibleLeftOutCount(count => count + RecipientPageSize)}
								/>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

const LeftOutRow = ({ contact }: { contact: IAICampaignRecipient }) => {
	const failed = contact.status === 'Failed';
	return (
		<div className={css(styleSheet.leftOutRow)}>
			<div className={css(styleSheet.leftOutHeading)}>
				<span className={css(styleSheet.recipientName)}>
					{contact.name || contact.emailAddress || contact.contactId}
				</span>
				<Pill
					color={failed ? 'red' : 'orange'}
					label={failed ? 'Couldn’t be written' : 'Left out by AI'}
					variant='label'
				/>
			</div>
			{!failed && !!contact.reason && <span className={css(styleSheet.leftOutReason)}>{contact.reason}</span>}
		</div>
	);
};

const RecipientRow = ({
	isSelected,
	onClick,
	recipient,
}: {
	isSelected: boolean;
	onClick: () => void;
	recipient: IAICampaignRecipient;
}) => {
	// A raw button rather than a components-v2 Button: this is a full-width two-line selectable list row, which none
	// of the Button variants model. aria-pressed carries the selected state that the styling shows.
	return (
		<button
			aria-pressed={isSelected}
			className={css(styleSheet.recipientRow, isSelected ? styleSheet.recipientRowSelected : null)}
			onClick={onClick}
			type='button'
		>
			<span className={css(styleSheet.recipientName)}>
				{recipient.name || recipient.emailAddress || recipient.contactId}
			</span>
			<span className={css(styleSheet.recipientSubject)}>
				{recipient.subject || <em>No subject</em>}
				{!recipient.hasContent && <span className={css(styleSheet.recipientMissing)}> (no email written)</span>}
			</span>
		</button>
	);
};
